import * as THREE from "three";

// Small procedural canvas textures (no image files, so the single-file build
// stays small): plywood, painted metal, concrete, fabric. Each is mostly
// light grey-to-white so the material color tints it. Seeded, so every load
// looks the same. Null outside a real browser (tests run in jsdom).

export function canvas2d(w: number, h: number): CanvasRenderingContext2D | null {
  if (typeof document === "undefined" || /jsdom/i.test(navigator.userAgent)) return null;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c.getContext("2d");
}

export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function finish(ctx: CanvasRenderingContext2D, srgb = true): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(ctx.canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  if (srgb) tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/**
 * Plywood ramp surface: one 1.22 m sheet per texture repeat, with wavy grain,
 * a few knots, skid marks and a seam around the edge. White-ish, tinted by
 * the material color.
 */
export function plywoodTexture(): THREE.CanvasTexture | null {
  const S = 512;
  const ctx = canvas2d(S, S);
  if (!ctx) return null;
  const r = rng(7);
  ctx.fillStyle = "#f4ece0";
  ctx.fillRect(0, 0, S, S);
  // Grain: long wavy streaks across the sheet.
  for (let i = 0; i < 140; i++) {
    const y0 = r() * S;
    const amp = 2 + r() * 6;
    const freq = 1 + r() * 3;
    const shade = 200 + Math.floor(r() * 35);
    ctx.strokeStyle = `rgba(${shade - 30},${shade - 55},${shade - 90},${0.12 + r() * 0.18})`;
    ctx.lineWidth = 1 + r() * 2.5;
    ctx.beginPath();
    for (let x = -8; x <= S + 8; x += 8) {
      const y = y0 + Math.sin((x / S) * Math.PI * 2 * freq + i) * amp;
      if (x === -8) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  // Knots.
  for (let i = 0; i < 3; i++) {
    const x = r() * S;
    const y = r() * S;
    for (let k = 4; k > 0; k--) {
      ctx.strokeStyle = `rgba(150,105,60,${0.12 + 0.05 * k})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.ellipse(x, y, 5 + k * 5, 3 + k * 2, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  // Wheel skid marks.
  for (let i = 0; i < 10; i++) {
    ctx.strokeStyle = `rgba(60,50,45,${0.04 + r() * 0.06})`;
    ctx.lineWidth = 6 + r() * 8;
    const x = r() * S;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.bezierCurveTo(x + (r() - 0.5) * 120, S / 3, x + (r() - 0.5) * 120, (2 * S) / 3, x + (r() - 0.5) * 80, S);
    ctx.stroke();
  }
  // Sheet seam and screws.
  ctx.strokeStyle = "rgba(90,65,40,0.55)";
  ctx.lineWidth = 3;
  ctx.strokeRect(1.5, 1.5, S - 3, S - 3);
  ctx.fillStyle = "rgba(80,80,90,0.7)";
  for (let i = 0; i < 8; i++) {
    const t = (i + 0.5) / 8;
    for (const [x, y] of [
      [t * S, 10],
      [10, t * S],
    ] as const) {
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  return finish(ctx);
}

/** Painted steel: smooth color with a few scuffs and chips showing bare metal. */
export function paintedMetalTexture(): THREE.CanvasTexture | null {
  const W = 256;
  const H = 256;
  const ctx = canvas2d(W, H);
  if (!ctx) return null;
  const r = rng(11);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);
  // Soft paint unevenness.
  for (let i = 0; i < 30; i++) {
    ctx.fillStyle = `rgba(0,0,0,${0.015 + r() * 0.02})`;
    ctx.beginPath();
    ctx.arc(r() * W, r() * H, 10 + r() * 30, 0, Math.PI * 2);
    ctx.fill();
  }
  // Scratches along the length (v) and chips of bare metal.
  for (let i = 0; i < 40; i++) {
    ctx.strokeStyle = `rgba(210,215,225,${0.3 + r() * 0.4})`;
    ctx.lineWidth = 1 + r();
    const x = r() * W;
    const y = r() * H;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + (r() - 0.5) * 8, y + 10 + r() * 40);
    ctx.stroke();
  }
  for (let i = 0; i < 12; i++) {
    ctx.fillStyle = "rgba(190,195,205,0.9)";
    ctx.beginPath();
    ctx.ellipse(r() * W, r() * H, 2 + r() * 4, 1 + r() * 3, r() * 3, 0, Math.PI * 2);
    ctx.fill();
  }
  return finish(ctx);
}

/**
 * Park concrete, one 4 m slab per repeat: speckle, a couple of hairline
 * cracks, faint stains and a sawn joint around the edge.
 */
export function concreteTexture(): THREE.CanvasTexture | null {
  const S = 512;
  const ctx = canvas2d(S, S);
  if (!ctx) return null;
  const r = rng(3);
  ctx.fillStyle = "#d8d2c8";
  ctx.fillRect(0, 0, S, S);
  // Big soft tone variation, then speckle.
  for (let i = 0; i < 40; i++) {
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
    const shade = 200 + Math.floor(r() * 40);
    g.addColorStop(0, `rgba(${shade},${shade - 4},${shade - 12},0.35)`);
    g.addColorStop(1, `rgba(${shade},${shade - 4},${shade - 12},0)`);
    ctx.save();
    ctx.translate(r() * S, r() * S);
    ctx.scale(40 + r() * 90, 40 + r() * 90);
    ctx.fillStyle = g;
    ctx.fillRect(-1, -1, 2, 2);
    ctx.restore();
  }
  for (let i = 0; i < 2600; i++) {
    const shade = 150 + Math.floor(r() * 100);
    ctx.fillStyle = `rgba(${shade},${shade - 4},${shade - 10},${0.25 + r() * 0.35})`;
    const s = 1 + r() * 2;
    ctx.fillRect(r() * S, r() * S, s, s);
  }
  // Stains: a couple of dark wet-looking blotches.
  for (let i = 0; i < 3; i++) {
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
    g.addColorStop(0, "rgba(120,110,100,0.22)");
    g.addColorStop(0.7, "rgba(120,110,100,0.1)");
    g.addColorStop(1, "rgba(120,110,100,0)");
    ctx.save();
    ctx.translate(r() * S, r() * S);
    ctx.rotate(r() * Math.PI);
    ctx.scale(25 + r() * 45, 15 + r() * 30);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(0, 0, 1, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  // Hairline cracks: jagged walks from a joint inward.
  ctx.strokeStyle = "rgba(110,102,94,0.3)";
  ctx.lineCap = "round";
  for (let i = 0; i < 2; i++) {
    let x = r() * S;
    let y = i === 0 ? 0 : S * r();
    let a = i === 0 ? Math.PI / 2 : 0;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(x, y);
    for (let k = 0; k < 24; k++) {
      a += (r() - 0.5) * 0.9;
      x += Math.cos(a) * 9;
      y += Math.sin(a) * 9;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  // Sawn joints (shared with the neighbouring slab, so half width each side).
  ctx.strokeStyle = "rgba(150,142,132,0.9)";
  ctx.lineWidth = 5;
  ctx.strokeRect(0, 0, S, S);
  return finish(ctx);
}

/**
 * Fabric weave for clothes: a fine cross-hatch on white. `twill` gives denim's
 * diagonal lines instead.
 */
export function fabricTexture(twill = false): THREE.CanvasTexture | null {
  const S = 128;
  const ctx = canvas2d(S, S);
  if (!ctx) return null;
  const r = rng(twill ? 21 : 17);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, S, S);
  ctx.lineWidth = 1;
  if (twill) {
    for (let i = -S; i < S * 2; i += 4) {
      ctx.strokeStyle = `rgba(0,0,30,${0.1 + r() * 0.1})`;
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + S, S);
      ctx.stroke();
    }
    for (let i = 0; i < 200; i++) {
      ctx.fillStyle = `rgba(255,255,255,${0.2 + r() * 0.3})`;
      ctx.fillRect(r() * S, r() * S, 1 + r() * 3, 1);
    }
  } else {
    for (let i = 0; i < S; i += 3) {
      ctx.strokeStyle = `rgba(0,0,0,${0.05 + r() * 0.05})`;
      ctx.beginPath();
      ctx.moveTo(0, i + 0.5);
      ctx.lineTo(S, i + 0.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(i + 0.5, 0);
      ctx.lineTo(i + 0.5, S);
      ctx.stroke();
    }
  }
  const tex = finish(ctx);
  tex.repeat.set(4, 4);
  return tex;
}

/**
 * Baked contact shadows for the floor: an alpha texture over the whole park,
 * dark and blurred around every footprint so things sit on the ground.
 * `footprints` are rectangles in a primitive's frame: origin, rotation, the
 * u range it covers and its half width along w.
 */
export function contactShadowTexture(
  bounds: number,
  footprints: { x: number; z: number; rot: number; u0: number; u1: number; hw: number; strength: number }[],
): THREE.CanvasTexture | null {
  const S = 512;
  const ctx = canvas2d(S, S);
  if (!ctx) return null;
  const px = S / (bounds * 2);
  // Opaque grayscale (alphaMap reads the green channel): white = shadow.
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, S, S);
  ctx.filter = `blur(${Math.max(2, Math.round(0.55 * px))}px)`;
  for (const f of footprints) {
    ctx.save();
    // Plane UVs: u follows +x, v follows -z (the plane is rotated -90 about x).
    ctx.translate((f.x + bounds) * px, (f.z + bounds) * px);
    ctx.rotate(f.rot);
    ctx.fillStyle = `rgba(255,255,255,${f.strength})`;
    const pad = 0.12 * px;
    ctx.fillRect(f.u0 * px - pad, -f.hw * px - pad, (f.u1 - f.u0) * px + pad * 2, f.hw * 2 * px + pad * 2);
    ctx.restore();
  }
  // Darken into the perimeter walls too.
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  const w = 0.35 * px;
  ctx.fillRect(0, 0, S, w);
  ctx.fillRect(0, S - w, S, w);
  ctx.fillRect(0, 0, w, S);
  ctx.fillRect(S - w, 0, w, S);
  ctx.filter = "none";
  const tex = new THREE.CanvasTexture(ctx.canvas);
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

/** A soft round shadow blob (grayscale, for an alphaMap), for the skater's contact shadow. */
export function blobTexture(): THREE.CanvasTexture | null {
  const S = 128;
  const ctx = canvas2d(S, S);
  if (!ctx) return null;
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  g.addColorStop(0, "#fff");
  g.addColorStop(0.5, "#8c8c8c");
  g.addColorStop(1, "#000");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, S, S);
  return new THREE.CanvasTexture(ctx.canvas);
}

/**
 * A pickup's face (see collectables.ts): a round badge with a bold label,
 * transparent outside the circle. Unlike the surface textures it's drawn
 * full color, not tinted.
 */
export function pickupTexture(label: string, bg: string, fg: string): THREE.CanvasTexture | null {
  const ctx = canvas2d(128, 128);
  if (!ctx) return null;
  ctx.beginPath();
  ctx.arc(64, 64, 58, 0, Math.PI * 2);
  ctx.fillStyle = bg;
  ctx.fill();
  ctx.lineWidth = 8;
  ctx.strokeStyle = "#1b1530";
  ctx.stroke();
  ctx.fillStyle = fg;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `italic 900 ${label.length > 1 ? 34 : 80}px system-ui, sans-serif`;
  ctx.lineWidth = 6;
  ctx.strokeText(label, 64, 68);
  ctx.fillText(label, 64, 68);
  const tex = new THREE.CanvasTexture(ctx.canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * A challenge hub's banner face (#87 part 3): "TIER N" big over
 * "CHALLENGES", in `ink` on a `bg` board with a `trim` border. Bold and
 * high-contrast so it reads from across the park.
 */
export function hubSignTexture(tier: number, bg: string, ink: string, trim: string): THREE.CanvasTexture | null {
  const ctx = canvas2d(512, 128);
  if (!ctx) return null;
  ctx.fillStyle = trim;
  ctx.fillRect(0, 0, 512, 128);
  ctx.fillStyle = bg;
  ctx.fillRect(10, 10, 492, 108);
  ctx.fillStyle = ink;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "italic 900 62px system-ui, sans-serif";
  ctx.fillText(`TIER ${tier}`, 256, 50);
  ctx.font = "800 28px system-ui, sans-serif";
  ctx.fillText("C H A L L E N G E S", 256, 98);
  const tex = new THREE.CanvasTexture(ctx.canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}
